import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CANAIS_COMISSAO,
  ROTULO_CANAL_COMISSAO,
  ROTULO_SITUACAO_PRAZO,
  ROTULO_STATUS_IMPLANTACAO,
  ROTULO_TIPO_CONTATO,
  ROTULO_TIPO_CONTRATO,
  STATUS_IMPLANTACAO,
  TIPOS_CONTATO,
  TIPOS_CONTRATO,
  formatarReais,
  type CanalComissao,
  type SituacaoPrazo,
  type StatusImplantacao,
} from '@reducee/contracts';
import { useState } from 'react';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CampoSelecao, CampoTexto, CampoTextoLongo } from '../componentes/Campo.tsx';
import { Dialogo } from '../componentes/Dialogo.tsx';
import { DocumentosDoContato } from '../componentes/DocumentosDoContato.tsx';
import { ParcelasDoContrato } from '../componentes/ParcelasDoContrato.tsx';
import {
  Aviso,
  CabecalhoDePagina,
  Botao,
  Cartao,
  Celula,
  Linha,
  Pilula,
  Tabela,
  data,
  type Tom,
} from '../componentes/Superficie.tsx';
import { PRODUTOS, comValorAtual } from '../dominio/catalogo.ts';
import { useOperadoras } from '../dominio/operadoras.ts';
import { dia, ler } from '../formulario.ts';

type Implantacao = {
  id: string;
  status: StatusImplantacao;
  contrato: { id: string; valorMensalCentavos: number; vigenciaInicio: string | null };
  contato: { id: string; nome: string };
  produto: string;
  operadora: string | null;
  canalComissao: CanalComissao;
  responsavel: { id: string; nome: string };
  prazoSla: string | null;
  situacaoPrazo: SituacaoPrazo;
  diasDePrazo: number;
  dataProtocolo: string | null;
  numeroProtocolo: string | null;
  dataEmissaoBoleto: string | null;
  vencimentoPrimeiro: string | null;
  pagamentoPrimeiroEm: string | null;
  proximoVencimento: string | null;
  observacoes: string | null;
  checklist: { concluidos: number; total: number; percentual: number };
};

type ItemChecklist = {
  id: string;
  ordem: number;
  texto: string;
  concluido: boolean;
  concluidoEm: string | null;
};

/**
 * A resposta do marco traz o que a listagem não traz: por que o contrato
 * ativou sem cronograma de comissão, quando foi o caso.
 */
type ResultadoDoPrimeiroBoleto = Implantacao & {
  semComissao?: 'JA_EXISTEM' | 'SEM_OPERADORA' | 'SEM_REGRA';
};

const FILTROS = [
  { valor: 'ABERTAS', rotulo: 'Abertas' },
  { valor: 'ATRASADAS', rotulo: 'Atrasadas' },
  { valor: 'AGUARDANDO_BOLETO', rotulo: 'Aguardando 1º boleto' },
  { valor: 'TODAS', rotulo: 'Todas' },
] as const;

const TOM_DO_PRAZO: Record<SituacaoPrazo, Tom> = {
  SEM_PRAZO: 'neutro',
  NO_PRAZO: 'sucesso',
  PROXIMO: 'alerta',
  ATRASADO: 'erro',
};

/**
 * Do fechamento à vigência.
 *
 * O marco que a tela cobra não é a conclusão do checklist: é o pagamento do
 * primeiro boleto, porque é ele que transforma venda fechada em produção paga
 * e abre o cronograma de comissão.
 */
export function PaginaImplantacao(): React.JSX.Element {
  const [filtro, definirFiltro] = useState<(typeof FILTROS)[number]['valor']>('ABERTAS');
  const [checklistAberto, definirChecklistAberto] = useState<Implantacao | null>(null);
  const [registrando, definirRegistrando] = useState<Implantacao | null>(null);
  const [atualizando, definirAtualizando] = useState<Implantacao | null>(null);
  const [registrandoOportunidade, definirRegistrandoOportunidade] = useState(false);

  const consulta = useQuery({
    queryKey: ['implantacoes', filtro],
    queryFn: () => chamarApi<Implantacao[]>(`/implantacoes?filtro=${filtro}`),
  });

  const implantacoes = consulta.data ?? [];

  return (
    <section>
      <CabecalhoDePagina
        area="Implantação"
        titulo="Do fechamento à vigência"
        descricao="O pagamento do primeiro boleto é a data-base do contrato."
        acoes={
          <Botao variante="primario" onClick={() => definirRegistrandoOportunidade(true)}>
            + Nova implantação
          </Botao>
        }
      />

      <Aviso tom="sucesso">
        <b>Regra automática:</b> ao confirmar o pagamento do 1º boleto, o sistema define início de
        vigência, próximo vencimento mensal e competência da produção/bonificação.
      </Aviso>

      <div className="flex flex-wrap gap-2">
        {FILTROS.map((opcao) => (
          <Botao
            key={opcao.valor}
            variante={filtro === opcao.valor ? 'primario' : 'secundario'}
            onClick={() => definirFiltro(opcao.valor)}
          >
            {opcao.rotulo}
          </Botao>
        ))}
      </div>

      <Cartao
        className="mt-3"
        titulo="Implantações"
        descricao="Vendas fechadas entram aqui automaticamente."
      >
        <Tabela
          colunas={[
            'Cliente',
            'Produto',
            'Operadora',
            'Responsável',
            'Status',
            'Checklist',
            'SLA',
            '1º boleto',
            'Pagamento',
            'Vigência',
            'Próx. venc.',
            'Ação',
          ]}
          vazio="Nenhuma implantação neste filtro."
          carregando={consulta.isLoading}
        >
          {implantacoes.map((implantacao) => (
            <Linha key={implantacao.id}>
              <Celula className="font-medium">{implantacao.contato.nome}</Celula>
              <Celula>{implantacao.produto}</Celula>
              <Celula>{implantacao.operadora ?? '—'}</Celula>
              <Celula>{implantacao.responsavel.nome}</Celula>
              <Celula>
                <Pilula tom={implantacao.status === 'VIGENCIA_ATIVA' ? 'sucesso' : 'alerta'}>
                  {ROTULO_STATUS_IMPLANTACAO[implantacao.status]}
                </Pilula>
              </Celula>
              <Celula>
                <Botao tamanho="mini" onClick={() => definirChecklistAberto(implantacao)}>
                  {implantacao.checklist.concluidos}/{implantacao.checklist.total} ·{' '}
                  {implantacao.checklist.percentual}%
                </Botao>
              </Celula>
              <Celula>
                <Pilula tom={TOM_DO_PRAZO[implantacao.situacaoPrazo]}>
                  {implantacao.situacaoPrazo === 'ATRASADO'
                    ? `${Math.abs(implantacao.diasDePrazo)}d atrasado`
                    : implantacao.situacaoPrazo === 'SEM_PRAZO'
                      ? ROTULO_SITUACAO_PRAZO.SEM_PRAZO
                      : `${implantacao.diasDePrazo}d`}
                </Pilula>
              </Celula>
              {/* Vencimento e pagamento são colunas distintas: a data prometida
                * e o que de fato aconteceu com ela. */}
              <Celula>{data(implantacao.vencimentoPrimeiro)}</Celula>
              <Celula>
                {implantacao.pagamentoPrimeiroEm ? (
                  <Pilula tom="sucesso">Pago {data(implantacao.pagamentoPrimeiroEm)}</Pilula>
                ) : (
                  <Botao tamanho="mini" onClick={() => definirRegistrando(implantacao)}>
                    Registrar
                  </Botao>
                )}
              </Celula>
              <Celula>{data(implantacao.contrato.vigenciaInicio)}</Celula>
              <Celula>{data(implantacao.proximoVencimento)}</Celula>
              <Celula>
                <Botao tamanho="mini" onClick={() => definirAtualizando(implantacao)}>
                  Atualizar
                </Botao>
              </Celula>
            </Linha>
          ))}
        </Tabela>
      </Cartao>

      <Cartao
        className="mt-3"
        titulo="Parcelas mensais dos contratos"
        descricao="Controle dos 12 primeiros meses. A 1ª parcela nasce paga quando o primeiro boleto é confirmado."
      >
        <ParcelasDoContrato />
      </Cartao>

      {checklistAberto ? (
        <Checklist
          implantacao={checklistAberto}
          aoFechar={() => definirChecklistAberto(null)}
        />
      ) : null}

      {registrando ? (
        <RegistroDoPrimeiroBoleto
          implantacao={registrando}
          aoFechar={() => definirRegistrando(null)}
        />
      ) : null}

      {atualizando ? (
        <JanelaDaImplantacao
          implantacao={atualizando}
          aoFechar={() => definirAtualizando(null)}
        />
      ) : null}

      {registrandoOportunidade ? (
        <JanelaDaImplantacao aoFechar={() => definirRegistrandoOportunidade(false)} />
      ) : null}
    </section>
  );
}

/**
 * A janela "Registrar / atualizar implantação" do protótipo, que serve aos
 * dois botões da tela.
 *
 * Criar uma Implantação solta não existe no domínio: pelo ADR 0006 ela nasce
 * do Contrato, e o Contrato nasce da Oportunidade que chega a Fechado. Então
 * "+ Nova implantação" registra a venda já fechada — cria Contato, Lead,
 * Oportunidade e a fecha — e o Contrato, a Implantação, o checklist e o
 * cronograma de comissão saem do mesmo caminho auditado de sempre. O que a
 * janela preenche depois é a Implantação recém-aberta.
 *
 * Em edição, os dados da venda são corrigíveis aqui — é nesta tela que o erro
 * aparece, ao conferir o boleto contra o cadastro. Cada um vai para o seu dono,
 * fora da Implantação; o valor, que decide Produção paga e Bonificação, só a
 * Administradora corrige.
 */
function JanelaDaImplantacao({
  implantacao,
  aoFechar,
}: {
  /** Ausente: a janela abre em branco, para registrar uma venda nova. */
  implantacao?: Implantacao;
  aoFechar: () => void;
}): React.JSX.Element {
  const { usuaria } = useSessao();
  const clienteDeConsultas = useQueryClient();
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';
  const criando = implantacao === undefined;

  const usuarias = useQuery({
    queryKey: ['usuarias'],
    queryFn: () => chamarApi<Array<{ id: string; nome: string; ativa: boolean }>>('/usuarias'),
  });
  const responsaveis = (usuarias.data ?? []).filter((uma) => uma.ativa);

  /**
   * Controlado, e não `defaultValue`: a lista de usuárias chega por consulta,
   * depois da primeira pintura. Num `<select>` não-controlado o valor inicial
   * já teria passado quando as opções aparecem, e o navegador assumiria a
   * primeira da lista — transferindo a condução da implantação sem que
   * ninguém pedisse.
   */
  const [responsavelId, definirResponsavelId] = useState(implantacao?.responsavel.id ?? '');
  const [pago, definirPago] = useState(Boolean(implantacao?.pagamentoPrimeiroEm));
  const jaPago = Boolean(implantacao?.pagamentoPrimeiroEm);
  const [canalComissao, definirCanalComissao] = useState<CanalComissao>(
    implantacao?.canalComissao ?? 'DIRETO_OPERADORA',
  );

  const salvar = useMutation({
    mutationFn: (campos: FormData) =>
      gravarImplantacao(campos, {
        implantacao,
        responsavelId,
        ehAdministradora,
        pago,
        canalComissao,
      }),
    onSuccess: async () => {
      // Registrar aqui mexe em seis listas, e nenhuma depende da outra.
      await Promise.all(
        CONSULTAS_AFETADAS.map((chave) =>
          clienteDeConsultas.invalidateQueries({ queryKey: [chave] }),
        ),
      );
      aoFechar();
    },
  });

  return (
    <Dialogo area="Pós-venda" titulo="Registrar / atualizar implantação" aoFechar={aoFechar}>
      <form
        data-teste="formulario-implantacao"
        onSubmit={(evento) => {
          evento.preventDefault();
          salvar.mutate(new FormData(evento.currentTarget));
        }}
      >
        <CamposDaOportunidade
          implantacao={implantacao}
          responsaveis={responsaveis}
          responsavelId={responsavelId}
          definirResponsavelId={definirResponsavelId}
          ehAdministradora={ehAdministradora}
        />

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <CampoTexto
            rotulo="Data de emissão do 1º boleto"
            name="dataEmissaoBoleto"
            type="date"
            defaultValue={dia(implantacao?.dataEmissaoBoleto ?? null)}
          />
          <CampoTexto
            rotulo="Vencimento do 1º boleto"
            name="vencimentoPrimeiro"
            type="date"
            defaultValue={dia(implantacao?.vencimentoPrimeiro ?? null)}
          />

          {/* Confirmado, continua corrigível: o pagamento gerou as 12 parcelas
            * e o cronograma de comissão, e corrigir refaz os dois. A rota
            * recusa quando algo do que foi gerado já andou. */}
          <CampoSelecao
            rotulo="1º boleto pago?"
            name="pago"
            value={pago ? 'sim' : 'nao'}
            onChange={(evento) => definirPago(evento.target.value === 'sim')}
          >
            <option value="nao">Não</option>
            <option value="sim">Sim</option>
          </CampoSelecao>
          <CampoTexto
            rotulo="Data do pagamento"
            name="pagamentoPrimeiroEm"
            type="date"
            required={pago}
            disabled={!pago}
            defaultValue={dia(implantacao?.pagamentoPrimeiroEm ?? null)}
            dica={
              jaPago
                ? 'Corrigir refaz as parcelas, a comissão e a renovação geradas pelo pagamento.'
                : undefined
            }
          />

          {/* Onde a venda foi cadastrada. Fica nesta tela porque é aqui que
            * alguém descobre, conferindo o boleto, que ela saiu pelo parceiro
            * e não direto — e ainda dá tempo de corrigir. Depois do pagamento
            * a comissão já nasceu pela regra do canal, e trocar não a refaz. */}
          <CampoSelecao
            rotulo="Canal de comissão"
            name="canalComissao"
            value={canalComissao}
            disabled={jaPago || !ehAdministradora}
            onChange={(evento) => definirCanalComissao(evento.target.value as CanalComissao)}
            dica={
              jaPago
                ? 'A comissão já foi gerada por este canal. Corrigir agora exige refazer o cronograma.'
                : 'Define, com a operadora, qual regra de comissão vale no pagamento do 1º boleto.'
            }
          >
            {CANAIS_COMISSAO.map((canal) => (
              <option key={canal} value={canal}>
                {ROTULO_CANAL_COMISSAO[canal]}
              </option>
            ))}
          </CampoSelecao>
          <CampoTexto
            rotulo="Início de vigência"
            name="vigenciaInicio"
            type="date"
            defaultValue={dia(implantacao?.contrato.vigenciaInicio ?? null)}
            dica="Preenchido sozinho com a data do pagamento. Informe só para corrigir."
          />

          <CampoSelecao
            rotulo="Status"
            name="status"
            defaultValue={implantacao?.status ?? STATUS_IMPLANTACAO[0]}
          >
            {STATUS_IMPLANTACAO.map((status) => (
              <option key={status} value={status}>
                {ROTULO_STATUS_IMPLANTACAO[status]}
              </option>
            ))}
          </CampoSelecao>
          <CampoTexto
            rotulo="Prazo de SLA"
            name="prazoSla"
            type="date"
            defaultValue={dia(implantacao?.prazoSla ?? null)}
          />

          <CampoTexto
            rotulo="Número do protocolo"
            name="numeroProtocolo"
            maxLength={80}
            defaultValue={implantacao?.numeroProtocolo ?? ''}
          />
          <CampoTexto
            rotulo="Data do protocolo"
            name="dataProtocolo"
            type="date"
            defaultValue={dia(implantacao?.dataProtocolo ?? null)}
          />
        </div>

        <CampoTextoLongo
          rotulo="Observações"
          name="observacoes"
          maxLength={2000}
          defaultValue={implantacao?.observacoes ?? ''}
          className="mt-3"
        />

        <div className="mt-4">
          <Aviso tom="alerta">
            Ao marcar <b>Pago</b>, a data do pagamento será usada como <b>início da vigência</b>,
            competência da produção e referência para os próximos vencimentos mensais.
          </Aviso>
        </div>

        {salvar.error ? (
          <p className="text-sm text-erro">
            {salvar.error instanceof ErroDaApi
              ? salvar.error.message
              : 'Não foi possível salvar a implantação.'}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2">
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao tipo="submit" variante="primario" desabilitado={salvar.isPending}>
            {salvar.isPending ? 'Salvando…' : 'Salvar implantação'}
          </Botao>
        </div>
      </form>
    </Dialogo>
  );
}

/**
 * O que a pessoa precisa saber antes de mexer no valor de uma venda registrada.
 *
 * Quem não pode corrigir descobre por quê, em vez de encontrar um campo cinza
 * sem explicação. Quem pode, e cujo primeiro boleto já foi pago, descobre o
 * alcance da correção antes de fazê-la: Produção paga e Bonificação acompanham
 * o número novo, o cronograma de comissão não — ele nasceu uma vez, no
 * pagamento, e não é refeito por uma edição de campo.
 */
function dicaDoValor(
  implantacao: Implantacao | undefined,
  ehAdministradora: boolean,
): string | undefined {
  if (implantacao === undefined) return undefined;
  if (!ehAdministradora) return 'Só a Administradora corrige o valor de uma venda registrada.';
  if (implantacao.pagamentoPrimeiroEm === null) return undefined;

  return 'O 1º boleto já foi pago: corrigir aqui acerta a Produção paga e a Bonificação, e não refaz a comissão já gerada.';
}

/**
 * O que a janela mudou nos dados da venda, comparado ao que está gravado.
 *
 * Só o que mudou entra no corpo. Campo igual reenviado custaria uma recusa
 * (o valor, que é da Administradora) e uma linha de auditoria dizendo que
 * alguém corrigiu algo para o mesmo que já era.
 */
function correcoesDaVenda(campos: FormData, alvo: Implantacao): Record<string, string> {
  const correcoes: Record<string, string> = {};

  const cliente = ler(campos, 'cliente');
  const produto = ler(campos, 'produto');
  const operadora = ler(campos, 'operadora');
  const valor = ler(campos, 'valor');

  if (cliente !== null && cliente !== alvo.contato.nome) correcoes.cliente = cliente;
  if (produto !== null && produto !== alvo.produto) correcoes.produto = produto;
  if (operadora !== null && operadora !== alvo.operadora) correcoes.operadora = operadora;

  /**
   * O campo está em reais e a rota espera reais; a leitura é que vem em
   * centavos. Comparar os dois em centavos evita tanto o falso "mudou" de
   * `"324.74" !== "324,74"` quanto o engano de devolver à rota o número que se
   * leu, que multiplicaria a venda por cem.
   */
  if (valor !== null && Math.round(Number(valor) * 100) !== alvo.contrato.valorMensalCentavos) {
    correcoes.valorMensal = valor;
  }

  return correcoes;
}

/**
 * Grava o que a janela preencheu, na ordem que o domínio exige.
 *
 * Três passos, porque são três fatos diferentes: registrar a venda (só quando
 * não existe implantação ainda), editar os campos da implantação, e confirmar
 * o pagamento do 1º boleto — que tem rota própria por gerar parcelas e
 * comissão, e por isso não é edição de campo.
 */
async function gravarImplantacao(
  campos: FormData,
  contexto: {
    implantacao?: Implantacao;
    responsavelId: string;
    ehAdministradora: boolean;
    pago: boolean;
    canalComissao: CanalComissao;
  },
): Promise<void> {
  const { implantacao, responsavelId, ehAdministradora, pago, canalComissao } = contexto;
  const alvo = implantacao ?? (await registrarOportunidadeFechada(campos, responsavelId));

  await chamarApi(`/implantacoes/${alvo.id}`, {
    metodo: 'PATCH',
    corpo: {
      status: String(campos.get('status')) as StatusImplantacao,
      ...(ehAdministradora && responsavelId ? { responsavelId } : {}),
      prazoSla: ler(campos, 'prazoSla'),
      dataProtocolo: ler(campos, 'dataProtocolo'),
      numeroProtocolo: ler(campos, 'numeroProtocolo'),
      dataEmissaoBoleto: ler(campos, 'dataEmissaoBoleto'),
      vencimentoPrimeiro: ler(campos, 'vencimentoPrimeiro'),
      vigenciaInicio: ler(campos, 'vigenciaInicio'),
      // Só quando mudou: a rota recusa trocar o canal de um contrato já
      // ativado, e reenviar o mesmo valor faria toda edição bater nisso.
      ...(canalComissao !== alvo.canalComissao ? { canalComissao } : {}),
      // Pelo mesmo motivo, e mais um: o valor é da Administradora, e reenviá-lo
      // igual faria toda edição da Comercial esbarrar na recusa.
      ...correcoesDaVenda(campos, alvo),
      observacoes: ler(campos, 'observacoes'),
    },
  });

  const pagoEm = ler(campos, 'pagamentoPrimeiroEm');
  const pagoAntes = alvo.pagamentoPrimeiroEm?.slice(0, 10) ?? null;

  // Já registrado, mudar é correção: desfaz o que o pagamento gerou e refaz.
  if (pagoAntes !== null) {
    const pagoDepois = pago ? pagoEm : null;
    if (pagoDepois !== pagoAntes) {
      await chamarApi(`/implantacoes/${alvo.id}/primeiro-boleto/correcao`, {
        metodo: 'POST',
        corpo: { pagoEm: pagoDepois },
      });
    }
    return;
  }

  if (pago && pagoEm) {
    await chamarApi(`/implantacoes/${alvo.id}/primeiro-boleto`, {
      metodo: 'POST',
      corpo: { pagoEm },
    });
  }
}

/** Listas que uma venda registrada aqui desatualiza. */
const CONSULTAS_AFETADAS = [
  'implantacoes',
  'dashboard',
  'parcelas',
  'oportunidades',
  'leads',
  'contatos',
];

/**
 * Os dados da venda, no topo da janela como no protótipo.
 *
 * Em edição continuam corrigíveis: pertencem ao Contato e à Oportunidade, e é
 * para lá que a rota os leva. A exceção é o valor, que também alimenta a cópia
 * do Contrato e por isso tem dono de papel.
 */
function CamposDaOportunidade({
  implantacao,
  responsaveis,
  responsavelId,
  definirResponsavelId,
  ehAdministradora,
}: {
  implantacao?: Implantacao;
  responsaveis: Array<{ id: string; nome: string }>;
  responsavelId: string;
  definirResponsavelId: (id: string) => void;
  ehAdministradora: boolean;
}): React.JSX.Element {
  const criando = implantacao === undefined;

  // Operadora e produto de um registro antigo podem não estar mais na lista;
  // mostrar assim mesmo, para a janela não reescrever o que já foi vendido.
  const cadastro = useOperadoras();
  const operadoras = comValorAtual(cadastro.nomes, implantacao?.operadora);
  const produtos = comValorAtual(PRODUTOS, implantacao?.produto);

  return (
    <>
      <div className="grid gap-3 md:grid-cols-[1fr_auto]">
        <CampoTexto
          rotulo="Cliente"
          name="cliente"
          required
          defaultValue={implantacao?.contato.nome ?? ''}
        />
        {/* Sem documento não dá para deduzir: quem digita é quem sabe. Sem
          * isso, todo cliente nascia empresa. */}
        {criando ? (
          <CampoSelecao rotulo="Pessoa ou empresa" name="tipoContato" defaultValue="EMPRESA">
            {TIPOS_CONTATO.map((tipo) => (
              <option key={tipo} value={tipo}>
                {ROTULO_TIPO_CONTATO[tipo]}
              </option>
            ))}
          </CampoSelecao>
        ) : null}
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {/* Começa em branco, e não na primeira da lista: o valor vai no
          * salvamento, e o chute viraria dado (NEX-228). A `key` refaz o campo
          * quando o cadastro chega, para o `defaultValue` valer. */}
        <CampoSelecao
          key={cadastro.pronta ? 'operadoras-prontas' : 'operadoras-carregando'}
          rotulo="Operadora"
          name="operadora"
          defaultValue={implantacao?.operadora ?? ''}
        >
          <option value="">— selecione —</option>
          {operadoras.map((nome) => (
            <option key={nome} value={nome}>
              {nome}
            </option>
          ))}
        </CampoSelecao>

        <CampoSelecao
          rotulo="Produto"
          name="produto"
          defaultValue={implantacao?.produto ?? PRODUTOS[0]}
        >
          {produtos.map((nome) => (
            <option key={nome} value={nome}>
              {nome}
            </option>
          ))}
        </CampoSelecao>

        {/* Reatribuir a condução é decisão da Administradora: a API ignora o
          * campo vindo de qualquer outro papel. */}
        <CampoSelecao
          rotulo="Responsável"
          name="responsavelId"
          value={responsavelId}
          disabled={!ehAdministradora}
          onChange={(evento) => definirResponsavelId(evento.target.value)}
        >
          <option value="">Selecione…</option>
          {responsaveis.map((uma) => (
            <option key={uma.id} value={uma.id}>
              {uma.nome}
            </option>
          ))}
        </CampoSelecao>

        {/* Da Produção paga sai a Bonificação de quem conduz a venda, e por
          * isso a rota recusa o valor vindo de qualquer outro papel. A Comercial
          * vê o número e pede a correção a quem pode fazê-la — travar sem dizer
          * por quê é o que fazia esta tela inteira parecer quebrada. */}
        <CampoTexto
          rotulo="Valor-base (R$)"
          name="valor"
          type="number"
          step="0.01"
          min="0"
          required
          disabled={!criando && !ehAdministradora}
          dica={dicaDoValor(implantacao, ehAdministradora)}
          defaultValue={
            implantacao ? (implantacao.contrato.valorMensalCentavos / 100).toFixed(2) : ''
          }
        />

        {criando ? (
          <CampoSelecao
            rotulo="Tipo de contrato"
            name="tipoContrato"
            className="md:col-span-2"
            dica="Define, com a operadora, qual regra de comissão vale nesta venda."
          >
            {TIPOS_CONTRATO.map((tipo) => (
              <option key={tipo} value={tipo}>
                {ROTULO_TIPO_CONTRATO[tipo]}
              </option>
            ))}
          </CampoSelecao>
        ) : null}
      </div>
    </>
  );
}

/**
 * Registra a venda já fechada e devolve a Implantação que ela abriu.
 *
 * Uma chamada só: a API percorre Contato, Lead, Oportunidade, Contrato e
 * Implantação numa transação. Fazer os passos daqui, um por vez, deixava
 * Contato e Oportunidade órfãos quando um deles falhava no meio.
 */
async function registrarOportunidadeFechada(
  campos: FormData,
  responsavelId: string,
): Promise<Implantacao> {
  return chamarApi<Implantacao>('/implantacoes', {
    metodo: 'POST',
    corpo: {
      cliente: ler(campos, 'cliente'),
      tipoContato: String(campos.get('tipoContato') ?? 'EMPRESA'),
      produto: ler(campos, 'produto'),
      operadora: ler(campos, 'operadora'),
      tipoContrato: String(campos.get('tipoContrato')),
      valorMensal: String(campos.get('valor') ?? ''),
      ...(responsavelId ? { responsavelId } : {}),
    },
  });
}

function Checklist({
  implantacao,
  aoFechar,
}: {
  implantacao: Implantacao;
  aoFechar: () => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();

  const consulta = useQuery({
    queryKey: ['checklist', implantacao.id],
    queryFn: () => chamarApi<ItemChecklist[]>(`/implantacoes/${implantacao.id}/checklist`),
  });

  /**
   * As vidas cobertas, para o envio dizer de quem é cada documento.
   *
   * A operadora cobra documentação de todo mundo quando o Contrato tem mais de
   * uma vida; com uma só, o seletor nem aparece.
   */
  const vidas = useQuery({
    queryKey: ['beneficiarios', implantacao.contato.id],
    queryFn: () =>
      chamarApi<Array<{ id: string; nome: string; ativo: boolean }>>(
        `/contatos/${implantacao.contato.id}/beneficiarios`,
      ),
  });

  const alternar = useMutation({
    mutationFn: ({ itemId, concluido }: { itemId: string; concluido: boolean }) =>
      chamarApi<ItemChecklist[]>(`/implantacoes/${implantacao.id}/checklist/${itemId}`, {
        metodo: 'PATCH',
        corpo: { concluido },
      }),
    onSuccess: (itens) => {
      clienteDeConsultas.setQueryData(['checklist', implantacao.id], itens);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['implantacoes'] });
    },
  });

  return (
    <Dialogo
      titulo={`${implantacao.contato.nome} · ${implantacao.produto}`}
      descricao={implantacao.operadora ?? 'Roteiro de conferência'}
      aoFechar={aoFechar}
    >
      <ul className="space-y-2">
        {(consulta.data ?? []).map((item) => (
          <li key={item.id}>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-linha p-3 hover:bg-marinho-suave">
              <input
                type="checkbox"
                checked={item.concluido}
                onChange={(evento) =>
                  alternar.mutate({ itemId: item.id, concluido: evento.target.checked })
                }
                className="mt-0.5"
              />
              <span>
                <span className={item.concluido ? 'line-through opacity-60' : 'font-medium'}>
                  {item.texto}
                </span>
                <span className="block text-xs text-texto-suave">
                  {item.concluido ? `Concluído em ${data(item.concluidoEm)}` : 'Pendente'}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
      {consulta.isLoading ? <p className="text-texto-suave">Carregando…</p> : null}

      {/*
        A documentação é pedida aqui, e não na Carteira: é a Implantação que a
        operadora cobra para ativar o contrato. Antes desta porta existir, quem
        estava nesta tela precisava sair dela, achar o Cliente e anexar lá — e o
        arquivo ficava marcado como "Cliente", sem ligação com o que o exigiu.
      */}
      <div className="mt-4">
        <DocumentosDoContato
          contatoId={implantacao.contato.id}
          permitirEnvio
          compacto
          titulo="Documentos da implantação"
          teste="implantacao-documentos"
          vinculo={{ categoria: 'IMPLANTACAO', implantacaoId: implantacao.id }}
          vidas={(vidas.data ?? []).filter((vida) => vida.ativo)}
        />
      </div>
    </Dialogo>
  );
}

function RegistroDoPrimeiroBoleto({
  implantacao,
  aoFechar,
}: {
  implantacao: Implantacao;
  aoFechar: () => void;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [pagoEm, definirPagoEm] = useState(() => new Date().toISOString().slice(0, 10));
  const [semRegra, definirSemRegra] = useState(false);

  const registrar = useMutation({
    mutationFn: () =>
      chamarApi<ResultadoDoPrimeiroBoleto>(`/implantacoes/${implantacao.id}/primeiro-boleto`, {
        metodo: 'POST',
        corpo: { pagoEm },
      }),
    onSuccess: (resultado) => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['implantacoes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['dashboard'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['parcelas'] });
      // A lista do Sales mostra o 1º boleto, e lê da Oportunidade.
      void clienteDeConsultas.invalidateQueries({ queryKey: ['oportunidades'] });

      /**
       * Segura o diálogo aberto quando não houve cronograma por falta de
       * regra. Fechar aqui é o que fez duas vendas da Hapvida passarem oito
       * dias sem comissão: o registro deu certo, a tela disse que deu certo, e
       * o que faltou não tinha onde aparecer.
       *
       * Só neste caso. Sem operadora ou cronograma já existente são estados
       * legítimos e não param ninguém.
       */
      if (resultado?.semComissao === 'SEM_REGRA') {
        definirSemRegra(true);
        return;
      }
      aoFechar();
    },
  });

  return (
    <Dialogo
      titulo="Registrar pagamento do 1º boleto"
      descricao={`${implantacao.contato.nome} · ${formatarReais(implantacao.contrato.valorMensalCentavos)} / mês`}
      aoFechar={aoFechar}
    >
      <p className="rounded-lg bg-dourado-suave p-3 text-sm">
        Este registro cria as 12 parcelas do contrato e o cronograma de comissão. Confira a data
        antes de confirmar.
      </p>

      <label className="mt-4 block text-sm font-medium">
        Data do pagamento
        <input
          type="date"
          value={pagoEm}
          onChange={(evento) => definirPagoEm(evento.target.value)}
          className="mt-1 w-full rounded-lg border border-linha px-3 py-2 outline-none focus:border-marinho-claro"
        />
      </label>

      {registrar.error ? (
        <p className="mt-3 text-sm text-erro">
          {registrar.error instanceof ErroDaApi
            ? registrar.error.message
            : 'Não foi possível registrar o pagamento.'}
        </p>
      ) : null}

      {semRegra ? (
        <div className="mt-4">
          <Aviso tom="alerta">
            <b>Pagamento registrado, mas sem comissão.</b> Não há regra de comissão cadastrada
            para <b>{implantacao.operadora ?? 'esta operadora'}</b> no canal{' '}
            <b>{ROTULO_CANAL_COMISSAO[implantacao.canalComissao]}</b>. As 12 parcelas e a vigência
            foram criadas normalmente; o cronograma de comissão, não. Cadastre a regra em
            Comissões — ela não gera o cronograma desta venda sozinha, porque a comissão nasce uma
            vez só, aqui.
          </Aviso>
        </div>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        {semRegra ? (
          <Botao variante="primario" onClick={aoFechar}>
            Entendi
          </Botao>
        ) : (
          <>
            <Botao onClick={aoFechar}>Cancelar</Botao>
            <Botao
              variante="primario"
              desabilitado={registrar.isPending}
              onClick={() => registrar.mutate()}
            >
              {registrar.isPending ? 'Registrando…' : 'Confirmar pagamento'}
            </Botao>
          </>
        )}
      </div>
    </Dialogo>
  );
}
